import { useId } from "react";

import type { DirectoryMatch, TicketDetail } from "../api/types";
import { can, useMe } from "../auth/session";
import { TextField } from "../ui/Field";
import { Select } from "../ui/Select";
import { useCreateGroup, useGroupOptions, type PersonChoice } from "./api";
import { DirectoryPicker } from "./DirectoryPicker";
import { PeopleField } from "./PeopleField";

/**
 * Who a ticket goes to (DECISIONS #48): a person, or a group. A new group can be made right
 * here, with a name and its members; anyone at Aspect can be either.
 */

export const NEW_GROUP = "new";

export interface AssignTargetValue {
  kind: "person" | "group";
  person: DirectoryMatch | null;
  /** An existing group's id, NEW_GROUP, or "" before choosing. */
  groupId: string;
  newName: string;
  newMembers: DirectoryMatch[];
}

/** Where the dialog starts: on a person, with the ticket's current group ready under "A group". */
export function startingTarget(ticket?: Pick<TicketDetail, "group">): AssignTargetValue {
  return { kind: "person", person: null, groupId: ticket?.group?.id ?? "", newName: "", newMembers: [] };
}

export function personChoice(person: DirectoryMatch): PersonChoice {
  return { user_id: person.user_id, object_id: person.object_id, email: person.email, name: person.name };
}

/** What's missing before it can be sent, or null. */
export function targetProblem(value: AssignTargetValue): string | null {
  if (value.kind === "person") return value.person ? null : "Choose a person.";
  if (!value.groupId) return "Choose a group.";
  if (value.groupId === NEW_GROUP) {
    if (value.newName.trim().length < 2) return "Give the new group a name of at least 2 characters.";
    if (value.newMembers.length === 0) return "Add at least one member to the new group.";
  }
  return null;
}

export type AssignBody = { group_id: string } | { owner_id: string } | { owner: PersonChoice };

/** Turn the choice into the request body, creating the new group first if there is one. */
export function useAssignBody() {
  const create = useCreateGroup();
  async function toBody(value: AssignTargetValue): Promise<AssignBody> {
    if (value.kind === "person" && value.person) {
      return value.person.user_id ? { owner_id: value.person.user_id } : { owner: personChoice(value.person) };
    }
    if (value.groupId === NEW_GROUP) {
      const group = await create.mutateAsync({ name: value.newName.trim(), members: value.newMembers.map(personChoice) });
      return { group_id: group.id };
    }
    return { group_id: value.groupId };
  }
  return { toBody, creating: create.isPending, createError: create.error };
}

export function AssignTarget({
  value,
  onChange,
  problem,
}: {
  value: AssignTargetValue;
  onChange: (value: AssignTargetValue) => void;
  problem?: string | null;
}) {
  const me = useMe();
  const id = useId();
  const groups = useGroupOptions();
  // Only staff make new groups; anyone passing a ticket on can pick an existing one.
  const canCreate = can(me, "use_workspace");
  const set = (patch: Partial<AssignTargetValue>) => onChange({ ...value, ...patch });

  return (
    <div className="assign-target">
      <div className="seg" role="radiogroup" aria-label="Assign to">
        {(
          [
            ["person", "A person"],
            ["group", "A group"],
          ] as const
        ).map(([kind, label]) => (
          <button
            key={kind}
            type="button"
            role="radio"
            aria-checked={value.kind === kind}
            className="seg-item"
            onClick={() => set({ kind })}
          >
            {label}
          </button>
        ))}
      </div>

      {value.kind === "person" ? (
        <DirectoryPicker
          label="Person"
          hint="Anyone at Aspect."
          value={value.person}
          onChange={(person) => set({ person })}
        />
      ) : (
        <>
          <div className="field">
            <label className="field-label" htmlFor={`${id}-group`}>
              Group
            </label>
            <Select
              id={`${id}-group`}
              label="Group"
              value={value.groupId}
              placeholder="Choose a group"
              onChange={(groupId) => set({ groupId })}
              groups={[
                {
                  options: (groups.data ?? []).map((group) => ({
                    value: group.id,
                    label: group.name,
                    detail: group.member_count === 1 ? "1 member" : `${group.member_count} members`,
                  })),
                },
                ...(canCreate ? [{ options: [{ value: NEW_GROUP, label: "New group…" }] }] : []),
              ]}
            />
            <span className="field-hint">All members are notified.</span>
          </div>
          {value.groupId === NEW_GROUP ? (
            <NewGroupFields
              name={value.newName}
              members={value.newMembers}
              onName={(newName) => set({ newName })}
              onMembers={(newMembers) => set({ newMembers })}
            />
          ) : null}
        </>
      )}
      {problem ? <span className="field-error">{problem}</span> : null}
    </div>
  );
}

/** A group's name and members: here, and in Admin → Groups. */
export function NewGroupFields({
  name,
  members,
  onName,
  onMembers,
}: {
  name: string;
  members: DirectoryMatch[];
  onName: (name: string) => void;
  onMembers: (members: DirectoryMatch[]) => void;
}) {
  return (
    <div className="new-group">
      <TextField label="Group name" value={name} maxLength={120} placeholder="e.g. Payroll queries" onChange={(event) => onName(event.target.value)} />
      <PeopleField label="Members" addLabel="Add a member" people={members} onChange={onMembers} />
    </div>
  );
}
